import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, ArrowLeft, Check } from 'lucide-react';

const BACKEND_URL =
  process.env.REACT_APP_API_URL || "https://avex-backend-u4ay.onrender.com";



const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Error fetching product:', error);
    }
  };

  const addToCart = () => {
    const cart = JSON.parse(localStorage.getItem('avex_cart') || '[]');
    const existingIndex = cart.findIndex(item => item.id === product.id);
    
    if (existingIndex >= 0) {
      cart[existingIndex].quantity += quantity;
    } else {
      cart.push({ ...product, quantity });
    }
    
    localStorage.setItem('avex_cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cartUpdated'));
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 3000);
  };

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#BFA15F] mx-auto"></div>
          <p className="mt-4 text-[#888888]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <Link
          to="/products"
          className="inline-flex items-center gap-2 text-[#4A4A4A] hover:text-[#BFA15F] mb-8 transition-colors"
          data-testid="back-to-products"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Products
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 bg-white p-8 border border-[#E5E5E5]">
          {/* Product Image */}
          <div className="relative">
            <div className="aspect-square bg-[#F0F0ED] overflow-hidden">
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-detail-image"
              />
            </div>
            {product.stock < 5 && (
              <div className="absolute top-4 right-4 bg-red-600 text-white px-4 py-2 text-sm font-medium">
                Only {product.stock} left in stock!
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            <div className="mb-4">
              <span className="text-sm text-[#BFA15F] uppercase tracking-wider font-medium">
                {product.category_name}
              </span>
            </div>
            
            <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-4" data-testid="product-detail-title">
              {product.name}
            </h1>
            
            <div className="text-3xl font-bold text-[#BFA15F] mb-6" data-testid="product-detail-price">
              NPR {product.price.toLocaleString()}
            </div>

            <p className="text-[#4A4A4A] mb-8 leading-relaxed">
              {product.description}
            </p>

            {/* Specifications */}
            <div className="mb-8 pb-8 border-b border-[#E5E5E5]">
              <h3 className="font-heading text-xl font-semibold mb-4">Specifications</h3>
              <div className="space-y-3">
                {product.wattage && (
                  <div className="flex justify-between">
                    <span className="text-[#888888]">Wattage</span>
                    <span className="font-medium">{product.wattage}</span>
                  </div>
                )}
                {product.material && (
                  <div className="flex justify-between">
                    <span className="text-[#888888]">Material</span>
                    <span className="font-medium">{product.material}</span>
                  </div>
                )}
                {product.application && (
                  <div className="flex justify-between">
                    <span className="text-[#888888]">Application</span>
                    <span className="font-medium">{product.application}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-[#888888]">Stock</span>
                  <span className="font-medium">{product.stock} available</span>
                </div>
              </div>
            </div>

            {/* Quantity & Add to Cart */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Quantity</label>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 border border-[#E5E5E5] hover:border-[#BFA15F] transition-colors"
                    data-testid="decrease-quantity"
                  >
                    -
                  </button>
                  <span className="w-12 text-center font-medium" data-testid="quantity-display">{quantity}</span>
                  <button
                    onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                    className="w-10 h-10 border border-[#E5E5E5] hover:border-[#BFA15F] transition-colors"
                    data-testid="increase-quantity"
                  >
                    +
                  </button>
                </div>
              </div>

              <button
                onClick={addToCart}
                disabled={product.stock === 0}
                className={`w-full py-4 uppercase tracking-widest text-sm font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                  product.stock === 0
                    ? 'bg-[#E5E5E5] text-[#888888] cursor-not-allowed'
                    : addedToCart
                    ? 'bg-[#4A7C59] text-white'
                    : 'bg-[#BFA15F] text-white hover:bg-[#A68B4F]'
                }`}
                data-testid="add-to-cart-btn"
              >
                {addedToCart ? (
                  <>
                    <Check className="h-5 w-5" /> Added to Cart
                  </>
                ) : product.stock === 0 ? (
                  'Out of Stock'
                ) : (
                  <>
                    <ShoppingCart className="h-5 w-5" /> Add to Cart
                  </>
                )}
              </button>

              <Link
                to="/cart"
                className="block w-full py-4 border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white text-center uppercase tracking-widest text-sm font-medium transition-all duration-300"
                data-testid="view-cart-btn"
              >
                View Cart
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;